/**
 * Hook for client-side RBAC v2 permission probing.
 *
 * Wraps GET /api/auth/permission and answers a single question: does the
 * authenticated caller hold the requested permission within the supplied
 * organisation? The hook also exposes a `loading` flag for the initial
 * round-trip and a `refetch` for explicit re-checks.
 *
 * GitLab #808 — when a `PermissionProvider` is mounted above and is holding
 * the same organisation, the answer comes from that provider instead: the
 * slug joins one batched request rather than issuing its own, and it is
 * revalidated on organisation switch, window focus and a short interval, so a
 * role change or a revocation reaches an open session without a re-login.
 * Outside a provider, or for a gate asking about a different organisation,
 * the original single-probe behaviour is used unchanged.
 */

import { useCallback, useContext, useEffect, useState } from 'react';
import { PermissionContext } from '@/lib/auth/PermissionProvider';

export interface UsePermissionResult {
  allowed: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface PermissionResponse {
  success: boolean;
  allowed?: boolean;
  error?: string;
}

export function usePermission(
  permissionSlug: string | null,
  organisationId: string | null | undefined,
): UsePermissionResult {
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // `tick` increments to force a refetch from `refetch()`; relying on a
  // separate dependency keeps the effect's dep list narrow and predictable.
  const [tick, setTick] = useState(0);

  const provider = useContext(PermissionContext);
  // The provider only answers for the organisation it currently holds; a gate
  // pointed at another org keeps its own probe so it cannot read the wrong map.
  const fromProvider =
    !!provider &&
    !!permissionSlug &&
    !!organisationId &&
    provider.orgId === organisationId;

  const refetch = useCallback(() => {
    if (fromProvider && provider) {
      provider.refresh();
      return;
    }
    setTick((n) => n + 1);
  }, [fromProvider, provider]);

  useEffect(() => {
    if (!fromProvider || !provider || !permissionSlug) return;
    provider.register(permissionSlug);
  }, [fromProvider, provider, permissionSlug]);

  useEffect(() => {
    if (fromProvider) return;
    if (!permissionSlug || !organisationId) {
      setAllowed(false);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      permission: permissionSlug,
      organization_id: organisationId,
    });

    fetch(`/api/auth/permission?${params.toString()}`)
      .then(async (res) => {
        const body = (await res.json()) as PermissionResponse;
        if (cancelled) return;
        if (!res.ok || !body.success) {
          setAllowed(false);
          setError(body.error ?? `Request failed with status ${res.status}`);
          return;
        }
        setAllowed(body.allowed === true);
      })
      .catch((err) => {
        if (cancelled) return;
        setAllowed(false);
        setError(err instanceof Error ? err.message : 'Permission check failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fromProvider, permissionSlug, organisationId, tick]);

  if (fromProvider && provider && permissionSlug) {
    // An absent entry means the batched request has not answered this slug
    // yet. Answers are held across a revalidation, so a refresh does not send
    // the gate back to `loading` and flicker the affordance away.
    const answer = provider.permissions[permissionSlug];
    return {
      allowed: answer === true,
      loading: answer === undefined,
      error: provider.error,
      refetch,
    };
  }

  return { allowed, loading, error, refetch };
}
